import * as yup from 'yup';

import { MINIMUM_AGE } from './constants';
import { calculateAge, isValidCnpj, isValidCpf, onlyDigits } from './masks';

const PIX_VALIDATORS: Record<string, (value: string) => boolean> = {
  CPF: (value) => isValidCpf(value),
  PHONE: (value) => [10, 11].includes(onlyDigits(value).length),
  EMAIL: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim()),
  RANDOM: (value) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      value.trim(),
    ),
};

export const deliveryPartnerSchema = yup.object({
  first_name: yup
    .string()
    .trim()
    .required('Informe seu nome')
    .min(2, 'Nome muito curto')
    .max(120, 'Nome muito longo'),
  last_name: yup
    .string()
    .trim()
    .required('Informe seu sobrenome')
    .min(2, 'Sobrenome muito curto')
    .max(120, 'Sobrenome muito longo'),
  cpf: yup
    .string()
    .required('Informe seu CPF')
    .test('cpf', 'CPF inválido', (value) => isValidCpf(value ?? '')),
  rg: yup
    .string()
    .transform((value) => (value ? value : undefined))
    .test(
      'rg',
      'RG inválido',
      (value) => !value || /^[0-9]{5,14}[0-9X]?$/.test(onlyDigitsOrX(value)),
    ),
  phone: yup
    .string()
    .required('Informe seu telefone')
    .test(
      'phone',
      'Telefone deve ter DDD e 10 ou 11 dígitos',
      (value) => [10, 11].includes(onlyDigits(value ?? '').length),
    ),
  email: yup
    .string()
    .trim()
    .required('Informe seu e-mail')
    .email('E-mail inválido')
    .max(180, 'E-mail muito longo'),
  birth_date: yup
    .string()
    .required('Informe sua data de nascimento')
    .test(
      'age',
      `É necessário ter no mínimo ${MINIMUM_AGE} anos`,
      (value) => calculateAge(value ?? '') >= MINIMUM_AGE,
    )
    .test(
      'plausible',
      'Data de nascimento inválida',
      (value) => calculateAge(value ?? '') < 100,
    ),

  modalidade: yup
    .string()
    .oneOf(['MEI', 'RPA'], 'Selecione a modalidade')
    .required('Selecione a modalidade'),
  cnpj: yup
    .string()
    .when('modalidade', {
      is: 'MEI',
      then: (schema) =>
        schema
          .required('Informe o CNPJ do seu MEI')
          .test('cnpj', 'CNPJ inválido', (value) => isValidCnpj(value ?? '')),
      otherwise: (schema) => schema.strip(),
    }),
  has_smartphone: yup.boolean().required('Selecione uma opção'),
  has_app: yup.boolean().required('Selecione uma opção'),
  accepts_contact: yup.boolean().required('Selecione uma opção'),

  bank_name: yup.string().required('Selecione o banco'),
  bank_code: yup.string().optional(),
  account_type: yup
    .string()
    .oneOf(['PF', 'PJ'], 'Selecione o tipo de conta')
    .required('Selecione o tipo de conta'),
  bank_agency: yup
    .string()
    .required('Informe a agência')
    .matches(/^\d{1,10}$/, 'Agência deve conter apenas números'),
  bank_account: yup
    .string()
    .required('Informe a conta com dígito')
    .matches(/^[\dxX-]{2,20}$/, 'Conta inválida'),
  has_pix: yup.boolean().required('Selecione uma opção'),
  pix_key_type: yup.string().when('has_pix', {
    is: true,
    then: (schema) =>
      schema
        .oneOf(['CPF', 'PHONE', 'EMAIL', 'RANDOM'], 'Selecione o tipo de chave')
        .required('Selecione o tipo de chave'),
    otherwise: (schema) => schema.strip(),
  }),
  pix_key: yup.string().when('has_pix', {
    is: true,
    then: (schema) =>
      schema.required('Informe sua chave PIX').test(
        'pix-format',
        'Chave PIX não corresponde ao tipo selecionado',
        function (value) {
          const type = this.parent.pix_key_type as string | undefined;
          const validate = type ? PIX_VALIDATORS[type] : undefined;
          return !validate || validate(value ?? '');
        },
      ),
    otherwise: (schema) => schema.strip(),
  }),

  regions: yup
    .array(yup.string().required())
    .min(1, 'Selecione ao menos uma região')
    .required('Selecione ao menos uma região'),
  available_days: yup
    .array(yup.string().required())
    .min(1, 'Selecione ao menos um dia')
    .required('Selecione ao menos um dia'),
  preferred_periods: yup
    .array(yup.string().required())
    .min(1, 'Selecione ao menos um período')
    .required('Selecione ao menos um período'),
  accepted_terms: yup
    .boolean()
    .oneOf([true], 'É necessário aceitar os Termos e Condições de Uso')
    .required(),
  accepted_safety: yup
    .boolean()
    .oneOf([true], 'É necessário declarar ciência das normas de segurança')
    .required(),
});

export type DeliveryPartnerFormValues = yup.InferType<
  typeof deliveryPartnerSchema
>;

/** Fields validated before the "Continuar" button advances each step. */
export const STEP_FIELDS: Array<Array<keyof DeliveryPartnerFormValues>> = [
  ['first_name', 'last_name', 'cpf', 'rg', 'phone', 'email', 'birth_date'],
  [
    'modalidade',
    'cnpj',
    'has_smartphone',
    'has_app',
    'accepts_contact',
    'bank_name',
    'account_type',
    'bank_agency',
    'bank_account',
    'has_pix',
    'pix_key_type',
    'pix_key',
  ],
  [
    'regions',
    'available_days',
    'preferred_periods',
    'accepted_terms',
    'accepted_safety',
  ],
];

function onlyDigitsOrX(value: string): string {
  return value.replace(/[^0-9Xx]/g, '').toUpperCase();
}
